import { fg } from '@atlassian/jira-feature-gating';
import {
	getIsInitialSpaRender,
	getIsInitialSpaRenderFromSelector,
} from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { getRouterState } from '@atlassian/react-resource-router';
import { getInitialLoadTaskId } from './getInitialLoadTaskId.tsx';

type CorrelatedTaskId = string | undefined;

type CorrelatedTaskIdInput = {
	experience: string;
	explicitTaskId?: CorrelatedTaskId;
};

/**
 * Attaches the correlating SSR `taskId` to a terminal event for the experience
 * the current route opts into SSR `taskStart` emission for (via
 * `analyticsOptions.taskStart` — the same route config the SSR side uses).
 *
 * The trace id is only attached on the initial SPA render, so SPA-to-SPA
 * transitions and embeds don't carry a stale SSR trace. When
 * `jfp-cmon-task-start-issue-view-initial-render` is on we use the pre-existing
 * transition-state signal; when off we use the interim helper.
 *
 * Falls back to any `explicitTaskId` provided by the caller.
 */
export const getCorrelatedTaskIdAttributes = ({
	experience,
	explicitTaskId,
}: CorrelatedTaskIdInput): { taskId?: CorrelatedTaskId } => {
	const isRouteExperienceMatch =
		getRouterState().route?.analyticsOptions?.taskStart?.experience === experience;

	// When on, use the pre-existing transition-state signal; when off, use the
	// interim helper added alongside SSR taskStart emission.
	const isInitialRender = fg('jfp-cmon-task-start-issue-view-initial-render')
		? getIsInitialSpaRenderFromSelector()
		: getIsInitialSpaRender();

	const shouldAttachTaskId =
		isRouteExperienceMatch && isInitialRender && fg('jfp-cmon-task-start-issue-view-gasv3');

	const taskId = (shouldAttachTaskId ? getInitialLoadTaskId() : undefined) ?? explicitTaskId;

	return taskId ? { taskId } : {};
};
